import React, { useState, useRef } from 'react';
import { Typography, Box, Button, Modal, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchNextDataWithRetry, fetchWrapperNoAuth, mergeObjects, SERVER } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import CustomCircularProgress from '../Loaders/CustomCircularProgress';

const VariantsModal = ({ product, variants, handleSelect }) => {
  const { theme } = useTheme();
  const { user, password } = useAuth();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    modal: {
      position: 'absolute',
      top: '53%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxHeight: '80vh',
      overflow: 'auto',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      outline: 'none',
    },
    container: {
      width: "90%",
      m: "5%",
      maxHeight: '55vh', // Adjust the height as needed
      overflowY: 'auto', // Enable vertical scrolling
      backgroundColor: theme.background.secondary,
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '3px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    th: {
      fontWeight: "bold",
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 18,
      position: "sticky", // Keeps the header in place
      top: 0, // Position at the top
      backgroundColor: theme.background.secondary, // Same background color for header
      zIndex: 1, // Ensures the header is above the scrollable body
    },
    td: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 16,
      cursor: 'pointer'
    },
    button: {
      display: "block",
      mt: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.color.primary}`,
      borderRadius: '10px',
      color: theme.color.primary,
      backgroundColor: 'transparent',
    },
    cancelButton: {
      width: '30%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.accent.error}`,
      borderRadius: '10px',
      color: theme.accent.error,
      backgroundColor: 'transparent',
    },
    text: {
      color: theme.text.primary,
      fontFamily: theme.fontFamily
    }
  };

  const handleChoose = async (variant) => {
    setIsLoading(true);
    try {
      let nextData = await fetchNextDataWithRetry(user, password, variant.usItemId);
      if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
        try {
          const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${nextData.upc}`, user, password);
          if (azData.fromAz) {
            if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
              nextData.dimsFromAz = true;
              nextData.length = azData.length;
              nextData.height = azData.height;
              nextData.width = azData.width;
            } else {
              nextData.dimsFromAz = false;
            }
            if (!nextData.weight && azData.weight) {
              nextData.weightFromAz = true;
              nextData.weight = azData.weight;
            } else {
              nextData.weightFromAz = false;
            }
            if (!nextData.manufacturer && azData.manufacturer) {
              nextData.manufacturerFromAz = true;
              nextData.manufacturer = azData.manufacturer;
            } else {
              nextData.manufacturerFromAz = false;
            }
            if (!nextData.brand && azData.brand) {
              nextData.brandFromAz = true;
              nextData.brand = azData.brand;
            } else {
              nextData.brandFromAz = false;
            }
          }
        } catch (error) {
          console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${nextData.upc}:`, error.message);
        }
      }

      try {
        nextData['image'] = nextData['images'][0]
      } catch {}
      const updatedItem = mergeObjects(product, nextData);
      updatedItem.itemId = variant.usItemId;
      setOpen(false);
      handleSelect(updatedItem);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Button variant="contained" sx={styles.button} onClick={() => setOpen(true)}>Variants</Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={styles.modal}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '45dvw', height: '45dvh' }}>
              <CustomCircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={styles.container}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '10%', ...styles.th, }}>Variant ID</TableCell>
                    <TableCell sx={{ width: '50%', ...styles.th }}>Variant Details</TableCell>
                    <TableCell sx={{ width: '25%', ...styles.th }}>Variant Price</TableCell>
                    <TableCell sx={{ width: '15%', ...styles.th }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variants.map((variant, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ textAlign: 'center' }}>
                          <img
                            id={`hover-img-${variant.usItemId}-variant`}
                            loading="lazy"
                            src={variant?.imageInfo?.thumbnailUrl || variant?.imageInfo?.allImages[0]?.url || "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg"}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // Prevents looping
                              currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
                            }}
                            style={{
                              borderRadius: "5vw",
                              border: `3px solid ${theme.color.primary}`,
                              height: "3vw",
                              width: "3vw",
                              objectFit: "scale-down",
                              position: "relative",
                              // zIndex: "5",
                            }}
                            onMouseEnter={() => {
                              const img = document.getElementById(`hover-img-${variant.usItemId}-variant`);
                              img.style.transform = "scale(1.5)";
                              img.style.boxShadow = "1px 1px 15px 1px gray";
                              img.style.backgroundColor = "white";
                              img.style.cursor = "pointer";
                              img.style.zIndex = "99999";
                            }}
                            onMouseLeave={() => {
                              const img = document.getElementById(`hover-img-${variant.usItemId}-variant`);
                              img.style.transform = "none";
                              img.style.boxShadow = "none";
                              img.style.backgroundColor = "none";
                              img.style.cursor = "none";
                              img.style.zIndex = "0";
                            }}
                            onClick={() => handleChoose(variant)}
                          />
                          <Typography sx={styles.text}>{variant.usItemId}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <Box>
                          {Object.values(variant.variants).map(v => { return { name: v.metadata.name, value: v.name } }).map(attr => (
                            <Typography sx={styles.text}><strong>{attr.name}</strong>: {attr.value}</Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.td}>
                        <Typography sx={styles.text}>{variant?.pricing?.priceString}</Typography>
                      </TableCell>
                      <TableCell>
                        <Button variant="contained" sx={styles.button} onClick={() => handleChoose(variant)}>Select</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', my: 2 }}>
            <Button
              variant="contained"
              sx={styles.cancelButton}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default VariantsModal;